import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-aria-components";
import { useRef as useReactRef } from "react";
import { FoundStatusSmartEnum } from "../../../enums/Domain/FoundStatusSmartEnum";
import { RecallLineItemStatusSmartEnum } from "../../../enums/Domain/RecallLineItemStatusSmartEnum";
import KeyBinds, { KeyActions } from "../../../modules/keybinds/keyBindList";
import { useKeyBind } from "../../../modules/keybinds/useKeyBind";
import { getFoundStatusFromQuantity } from "../layout/SidePanel/layouts/SidePanelRecallPickList";
import { IPicklistItem } from "../List/RecallsPickList";
import LoadingButton from "../LoadingButton";

interface IPickingButtonProps {
    lineItems: IPicklistItem[];
    save: Function;
    submit: Function;
    cancel?: Function;
    Next?: () => void;
    Prev?: () => void;
}

export default function RecallPickingButtons({ lineItems, save, submit, cancel, Next, Prev }: IPickingButtonProps) {
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const prevRef = useRef(Prev);
    const nextRef = useRef(Next);
    const nextButtonRef = useReactRef<HTMLButtonElement>(null);
    const prevButtonRef = useReactRef<HTMLButtonElement>(null);
    const saveButtonRef = useReactRef<HTMLButtonElement>(null);
    const submitButtonRef = useReactRef<HTMLButtonElement>(null);

    useEffect(() => {
        prevRef.current = Prev;
        nextRef.current = Next;
    }, [Prev, Next]);

    // Set tabIndex on button elements
    useEffect(() => {
        if (nextButtonRef.current) {
            nextButtonRef.current.tabIndex = 3;
        }
        if (prevButtonRef.current) {
            prevButtonRef.current.tabIndex = -1;
        }
        if (saveButtonRef.current) {
            saveButtonRef.current.tabIndex = -1;
        }
        if (submitButtonRef.current) {
            submitButtonRef.current.tabIndex = -1;
        }
    });

    useKeyBind(KeyBinds[KeyActions.MoveToPreviousListItem], (e: KeyboardEvent) => {
        if (prevRef.current) {
            prevRef.current();
        }
    });

    useKeyBind(KeyBinds[KeyActions.MoveToNextListItem], (e: KeyboardEvent) => {
        if (nextRef.current) {
            nextRef.current();
        }
    });

    async function handleSave() {
        setSaving(true);
        await save(lineItems);
        setSaving(false);
    }

    async function handleSubmission(func: Function) {
        setSubmitting(true);
        await func(lineItems);
        setSubmitting(false);
    }

    function undecidedItemCount(data: IPicklistItem[]) {
        return data.filter((x: IPicklistItem) =>
            getFoundStatusFromQuantity(x.quantity).value === FoundStatusSmartEnum.Undecided.value &&
            x.status.value !== RecallLineItemStatusSmartEnum.Cancelled.value)?.length ?? 0;
    }

    return (
        <>
            <div>
                {undecidedItemCount(lineItems) > 0 && (
                    <div className="form__inline-error margin__bottom--xs">
                        <p className="">All items must be actioned to save and submit. {undecidedItemCount(lineItems)} remaining</p>
                    </div>
                )}
            </div>
            <div className="display--flex justifyContent--space-between margin__bottom--xxs">
                {cancel ? (
                    <Button 
                        type="button" 
                        className="button button--secondary button--secondary--destructive button--small" 
                        onPress={() => handleSubmission(cancel)} 
                        isDisabled={submitting}
                    >
                        <p className="button-text">Cancel Recall</p>
                    </Button>
                ) : (
                    <div className="display--flex gap--xs">
                        <Button ref={prevButtonRef} isDisabled={!Prev} type="button" className="button button--secondary button--small" onPress={Prev ? () => Prev() : undefined}>
                            <p className="button-text">Previous</p>
                        </Button>
                        <Button ref={nextButtonRef} isDisabled={!Next} type="button" className="button button--secondary button--small" onPress={Next ? () => Next() : undefined}>
                            <p className="button-text">Next</p>
                        </Button>
                    </div>
                )}
                <div className="display--flex gap--xs">
                    <LoadingButton 
                        ref={saveButtonRef}
                        className="button button--secondary button--small" 
                        onPress={() => handleSave()}
                        isDisabled={saving || submitting}
                        isLoading={saving}
                        buttonText="Save"
                        loadingText="Saving"
                    >
                        <></>
                    </LoadingButton>
                    <Button 
                        ref={submitButtonRef}
                        className="button button--primary button--small" 
                        onPress={() => handleSubmission(submit)}
                        isDisabled={saving || submitting || undecidedItemCount(lineItems) > 0}
                    >
                        <p className="button-text">Save & Submit</p>
                    </Button>
                </div>
            </div>
        </>
    );
}